import * as React from "react";
import { Checkbox, useFormState, Option, useFormApi, Form } from "informed";
import { Link, useParams } from "react-router-dom";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { Dropdown } from "../../../forms/informedv2/Dropdown";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { ActionButtons } from "../../../misc/ActionButtons";
import { ChallengeDecisionMaker, StudyPressureSetting } from "../../../misc/api/apiTypes";
import { ParameterEntity } from "../../../misc/api/SerenityReferenceData";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { ConvertToIdsArray, ConvertToSelectListItemArray } from "./ParameterSelectList";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

interface SnipSettingsScreenFormState {
    acceptableTests: number;
    maximumTests: number;
    testAcceptanceChange: boolean;
    testAcceptanceChangeWindow: number;
    decisionMaker: ChallengeDecisionMaker;
    overreadEnabled: boolean;
    selectedTestParameters: ISelectListItem[];
    selectedAnalysisParameters: ISelectListItem[];
    selectedRepeatabilityParameters: ISelectListItem[];
}

interface SnipSettingsScreenFormProps {
    studyId: string;
    parameters: ParameterEntity[];
}

export const SnipSettingsScreenForm = ({ studyId, parameters }: SnipSettingsScreenFormProps) => {
    const formApi = useFormApi<SnipSettingsScreenFormState>();
    const formState = useFormState<SnipSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    const analysisParametersSelectList = parametersSelectList.filter(p => !p.Text.includes("Predicted") && !p.Text.includes("Repeatability"));
    const repeatabilityParametersSelectList = parametersSelectList.filter(p => p.Text.includes("Repeatability"));

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {

        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const snipSettings: StudyPressureSetting = {
            SelectedTestParameters: ConvertToIdsArray(state.values.selectedTestParameters, parameters),
            AcceptableTests: state.values.acceptableTests,
            MaxTests: state.values.maximumTests,
            SelectedFinalTestAcceptabilityDecisionMaker: state.values.decisionMaker,
            IsOverreadingAvailable: state.values.overreadEnabled,
            TestAcceptanceChange: state.values.testAcceptanceChange,
            TestAcceptanceChangeTimeWindow: state.values.testAcceptanceChangeWindow,
            SelectedAnalysisParameters: ConvertToIdsArray(state.values.selectedAnalysisParameters, parameters),
            SelectedRepeatabilityParameters: ConvertToIdsArray(state.values.selectedRepeatabilityParameters, parameters),
        }

        const payload = JSON.stringify(snipSettings);

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Pressure.StudySnipSetting", payload);

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    };

    const AcceptableToMaximumValidator = (value: number, threshold: number) => {
        return !value || value > threshold ? "'Acceptable Tests' field cannot be greater than the 'Maximum Tests' field" : undefined;
    }

    const acceptableTestValidation = (value: number, values: SnipSettingsScreenFormState) => {
        return IntegerValidator(value) || MinMaxValidator(value, 1, 8) || AcceptableToMaximumValidator(values.acceptableTests, values.maximumTests)
    }

    const maximumTestValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 4, 8)
    }

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">SNIP Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Acceptable Tests</label>
                                    <p className="help-block">Configures the number of acceptable tests required to comprise a valid session</p>
                                    <TextBox<number, SnipSettingsScreenFormState> id="acceptableTests" field="acceptableTests" type="number" min="1" max="8" required validate={acceptableTestValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Maximum Tests</label>
                                    <p className="help-block">Configures the maximum number of tests allowed</p>
                                    <TextBox<number, SnipSettingsScreenFormState> id="maximumTests" field="maximumTests" type="number" min="4" max="8" required validate={maximumTestValidation} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Test Acceptance Change Allowed</label>
                                    <p className="help-block">Configures whether test accceptance changes are allowed and the window of time after session completion</p>
                                    <Checkbox<boolean, SnipSettingsScreenFormState> id="testAcceptanceChange" field="testAcceptanceChange" />
                                    <TextBoxWithStaticSuffix<number, SnipSettingsScreenFormState> id="testAcceptanceChangeWindow" field="testAcceptanceChangeWindow" type="number" min="1" max="120" staticpart="Hours" disabled={!formState.values.testAcceptanceChange} validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 1, 120)} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens for supported applications</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={analysisParametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Overreading</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Over-Read enabled</label>
                                    <Checkbox id="overreadEnabledToggle" className="checkbox" field="overreadEnabled" />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Final Test Acceptability Decision Maker</label>
                                    <p className="help-block">Configures whether the final test acceptability is make by the Over-Reader or the Technician</p>
                                    <Dropdown id="decisionMaker" field="decisionMaker" disabled={!formState.values.overreadEnabled}>
                                        <Option id="Overreader">Over-reader</Option>
                                        <Option id="Technician">Technician</Option>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Analysis Parameters</label>
                                    <p className="help-block">The test parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedAnalysisParameters"
                                        field="selectedAnalysisParameters"
                                        options={analysisParametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Repeatability Parameters</label>
                                    <p className="help-block">The repeatability parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedRepeatabilityParameters"
                                        field="selectedRepeatabilityParameters"
                                        options={repeatabilityParametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const SnipSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<SnipSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const asyncEffect = async () => {
            const getParameters = async () => {
                const snipParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Snip);
                setParameters(snipParametersResult);
                return snipParametersResult;
            }

            const hydrate = async (snipSettings: StudyPressureSetting, parameterResponse: ParameterEntity[]) => {
                setInitialValues({
                    acceptableTests: snipSettings.AcceptableTests,
                    maximumTests: snipSettings.MaxTests,
                    overreadEnabled: snipSettings.IsOverreadingAvailable,
                    testAcceptanceChange: snipSettings.TestAcceptanceChange,
                    testAcceptanceChangeWindow: snipSettings.TestAcceptanceChangeTimeWindow,
                    selectedTestParameters: ConvertToSelectListItemArray(snipSettings.SelectedTestParameters, parameterResponse),
                    decisionMaker: snipSettings.SelectedFinalTestAcceptabilityDecisionMaker,
                    selectedAnalysisParameters: ConvertToSelectListItemArray(snipSettings.SelectedAnalysisParameters, parameterResponse),
                    selectedRepeatabilityParameters: ConvertToSelectListItemArray(snipSettings.SelectedRepeatabilityParameters, parameterResponse)
                });
            }

            const parameterResponse = await getParameters();
            const snipSettings = await legacyClient.getStudySnipSettings(id);
            hydrate(snipSettings, parameterResponse);
        }

        asyncEffect();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading SNIP Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
            selectedAnalysisParameters: values.selectedAnalysisParameters === null ? "At least one value must be selected" : undefined,
            selectedRepeatabilityParameters: values.selectedRepeatabilityParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form id="snipSettingsForm" noValidate initialValues={initialValues} validateFields={validateFields}>
            <SnipSettingsScreenForm studyId={id} parameters={parameters} />
        </Form>
    );
}
